/**
 * Admin Icon Creator — silhouette editor for PLAY hub category icons.
 */
import {
  fetchAdminHubIcons,
  publishAdminHubIcon,
  revertAdminHubIcon,
  type HubIconCategoryId,
} from "../lib/cloud";
import {
  HUB_ICON_CATEGORIES,
  HUB_ICON_CATEGORY_LABELS,
  HUB_ICON_H,
  HUB_ICON_W,
} from "../../lib/hubIconTypes";
import {
  centerMaskImageData,
  cloneMaskImageData,
  downscaleImageToHubIconGrid,
  fitMaskImageDataToHubCanvas,
  hubIconResolutionOptions,
  imageDataToLuminanceMask,
  loadImageDataFromDataUrl,
  maskDataUrlFromImageData,
  maskImageDataEmpty,
  normalizeMaskImageData,
} from "../../lib/hubIconMask";
import {
  applyHubIconToCategory,
  refreshAllHubIcons,
  setHubIconSessionOverride,
  setPublishedHubIcon,
  removePublishedHubIcon,
} from "./hub-icons";

const IMPORT_MAX_BYTES = 8 * 1024 * 1024;
const IMPORT_ACCEPT_MIME = new Set(["image/png", "image/jpeg"]);
const IMPORT_ACCEPT_EXT = new Set([".png", ".jpg", ".jpeg"]);

type Tool = "pencil" | "line" | "bucket" | "eraser";

const MAX_MASK_HISTORY = 48;

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

function importFileExtension(name: string): string {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
}

function validateImportImageFile(file: File): string | null {
  const ext = importFileExtension(file.name);
  const mimeOk = IMPORT_ACCEPT_MIME.has(file.type);
  const extOk = IMPORT_ACCEPT_EXT.has(ext);
  if (!mimeOk && !extOk) return "Only PNG and JPG images are supported.";
  if (file.type && !mimeOk) return "Only PNG and JPG images are supported.";
  if (file.size > IMPORT_MAX_BYTES) {
    const mb = Math.round(IMPORT_MAX_BYTES / (1024 * 1024));
    return `Image is too large (max ${mb} MB).`;
  }
  return null;
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read that image."));
    };
    img.src = url;
  });
}

export function mountAdminIconCreator(root: HTMLElement): { refresh: () => void } {
  root.replaceChildren();

  let selectedCategory: HubIconCategoryId = "modes";
  let tool: Tool = "pencil";
  let pencilSize = 1;
  /** Brush shade 1–255 (alpha stored in mask). */
  let brushOpacity = 255;
  let drawing = false;
  let lineStart: [number, number] | null = null;
  let lineEnd: [number, number] | null = null;

  type FloatingImport = {
    source: ImageData;
    x: number;
    y: number;
    w: number;
    h: number;
  };
  let floating: FloatingImport | null = null;
  let floatDrag: {
    mode: "move" | "resize";
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    origW: number;
    origH: number;
  } | null = null;

  const maskHistory: ImageData[] = [];
  let maskHistoryIndex = -1;

  const maskCanvas = document.createElement("canvas");
  maskCanvas.width = HUB_ICON_W;
  maskCanvas.height = HUB_ICON_H;
  const maskCtx = maskCanvas.getContext("2d")!;

  /** In-memory mask buffer — flushed to canvas on redraw / save. */
  let maskBuf: ImageData | null = null;

  function invalidateMaskBuf() {
    maskBuf = null;
  }

  function ensureMaskBuf(): ImageData {
    if (!maskBuf) maskBuf = maskCtx.getImageData(0, 0, HUB_ICON_W, HUB_ICON_H);
    return maskBuf;
  }

  function flushMaskBuf() {
    if (maskBuf) {
      maskCtx.putImageData(maskBuf, 0, 0);
      maskBuf = null;
    }
  }

  const published = new Map<string, string>();

  const wrap = document.createElement("div");
  wrap.className = "adminIconCreator adminShopCreator";

  const header = document.createElement("div");
  header.className = "adminIconCreatorHeader";
  header.innerHTML = `<h2>Icon Creator</h2>
    <p class="accessHint">Design luminance masks for PLAY hub categories — shade levels become tones of each player&apos;s Text color at runtime. Paint covers the full category card.</p>`;
  wrap.appendChild(header);

  const layout = document.createElement("div");
  layout.className = "adminIconCreatorLayout";

  const left = document.createElement("div");
  left.className = "adminIconCreatorMain";

  const catRow = document.createElement("div");
  catRow.className = "formRow adminIconCreatorCatRow";
  const catLabel = document.createElement("span");
  catLabel.className = "formLabel";
  catLabel.textContent = "Category";
  const catSelect = document.createElement("select");
  catSelect.className = "formInput adminShopToolSelect";
  catSelect.setAttribute("aria-label", "Hub category");
  HUB_ICON_CATEGORIES.forEach((id) => {
    const o = document.createElement("option");
    o.value = id;
    o.textContent = HUB_ICON_CATEGORY_LABELS[id];
    catSelect.appendChild(o);
  });
  catRow.appendChild(catLabel);
  catRow.appendChild(catSelect);
  left.appendChild(catRow);

  const toolbar = document.createElement("div");
  toolbar.className = "adminShopCreatorToolbar adminIconCreatorToolbar";

  const toolGroup = document.createElement("div");
  toolGroup.className = "adminShopToolbarGroup";
  const toolLabel = document.createElement("span");
  toolLabel.className = "adminShopToolbarLabel";
  toolLabel.textContent = "Tool";
  const toolSelect = document.createElement("select");
  toolSelect.className = "formInput adminShopToolSelect";
  (["pencil", "line", "bucket", "eraser"] as Tool[]).forEach((t) => {
    const o = document.createElement("option");
    o.value = t;
    o.textContent = t.charAt(0).toUpperCase() + t.slice(1);
    toolSelect.appendChild(o);
  });
  toolGroup.appendChild(toolLabel);
  toolGroup.appendChild(toolSelect);

  const importBtn = document.createElement("button");
  importBtn.type = "button";
  importBtn.className = "btn adminShopImportBtn";
  importBtn.textContent = "Import";
  toolGroup.appendChild(importBtn);
  const importFileInput = document.createElement("input");
  importFileInput.type = "file";
  importFileInput.accept = "image/png,image/jpeg,.png,.jpg,.jpeg";
  importFileInput.className = "hidden";
  toolGroup.appendChild(importFileInput);

  const editActionsGroup = document.createElement("div");
  editActionsGroup.className = "adminShopToolbarGroup adminShopToolbarGroup--actions";
  const undoBtn = document.createElement("button");
  undoBtn.type = "button";
  undoBtn.className = "btn";
  undoBtn.textContent = "Undo";
  undoBtn.disabled = true;
  const redoBtn = document.createElement("button");
  redoBtn.type = "button";
  redoBtn.className = "btn";
  redoBtn.textContent = "Redo";
  redoBtn.disabled = true;
  const centerBtn = document.createElement("button");
  centerBtn.type = "button";
  centerBtn.className = "btn";
  centerBtn.textContent = "Center";
  centerBtn.title = "Center the drawing in the category art area without resizing it";
  editActionsGroup.appendChild(undoBtn);
  editActionsGroup.appendChild(redoBtn);
  editActionsGroup.appendChild(centerBtn);

  const placeGroup = document.createElement("div");
  placeGroup.className = "adminShopToolbarGroup adminShopToolbarGroup--actions adminIconCreatorPlaceGroup hidden";
  const placeBtn = document.createElement("button");
  placeBtn.type = "button";
  placeBtn.className = "btn green";
  placeBtn.textContent = "Place";
  placeBtn.title = "Stamp the imported image onto the canvas";
  const discardImportBtn = document.createElement("button");
  discardImportBtn.type = "button";
  discardImportBtn.className = "btn";
  discardImportBtn.textContent = "Discard";
  const importScaleLabel = document.createElement("span");
  importScaleLabel.className = "adminShopToolbarLabel";
  importScaleLabel.textContent = "Import size";
  const importScaleSlider = document.createElement("input");
  importScaleSlider.type = "range";
  importScaleSlider.min = "10";
  importScaleSlider.max = "200";
  importScaleSlider.value = "100";
  importScaleSlider.className = "adminShopSizeSlider";
  importScaleSlider.setAttribute("aria-label", "Imported image size");
  const importScaleDisplay = document.createElement("span");
  importScaleDisplay.className = "adminShopToolbarLabel adminShopSizeDisplay";
  importScaleDisplay.textContent = "100%";
  placeGroup.appendChild(placeBtn);
  placeGroup.appendChild(discardImportBtn);
  placeGroup.appendChild(importScaleLabel);
  placeGroup.appendChild(importScaleSlider);
  placeGroup.appendChild(importScaleDisplay);

  const sizeGroup = document.createElement("div");
  sizeGroup.className = "adminShopToolbarGroup";
  const sizeLabel = document.createElement("span");
  sizeLabel.className = "adminShopToolbarLabel";
  sizeLabel.textContent = "Size";
  const sizeSlider = document.createElement("input");
  sizeSlider.type = "range";
  sizeSlider.min = "1";
  sizeSlider.max = "8";
  sizeSlider.value = "1";
  sizeSlider.className = "adminShopSizeSlider";
  const sizeDisplay = document.createElement("span");
  sizeDisplay.className = "adminShopToolbarLabel adminShopSizeDisplay";
  sizeDisplay.textContent = "1px";
  sizeGroup.appendChild(sizeLabel);
  sizeGroup.appendChild(sizeSlider);
  sizeGroup.appendChild(sizeDisplay);

  const shadeGroup = document.createElement("div");
  shadeGroup.className = "adminShopToolbarGroup";
  const shadeLabel = document.createElement("span");
  shadeLabel.className = "adminShopToolbarLabel";
  shadeLabel.textContent = "Shade";
  const shadeSlider = document.createElement("input");
  shadeSlider.type = "range";
  shadeSlider.min = "8";
  shadeSlider.max = "255";
  shadeSlider.value = "255";
  shadeSlider.className = "adminShopSizeSlider";
  shadeSlider.setAttribute("aria-label", "Brush shade opacity");
  const shadeDisplay = document.createElement("span");
  shadeDisplay.className = "adminShopToolbarLabel adminShopSizeDisplay";
  shadeDisplay.textContent = "100%";
  shadeGroup.appendChild(shadeLabel);
  shadeGroup.appendChild(shadeSlider);
  shadeGroup.appendChild(shadeDisplay);

  toolbar.appendChild(toolGroup);
  toolbar.appendChild(editActionsGroup);
  toolbar.appendChild(placeGroup);
  toolbar.appendChild(sizeGroup);
  toolbar.appendChild(shadeGroup);
  left.appendChild(toolbar);

  const workspace = document.createElement("div");
  workspace.className = "adminShopCreatorWorkspace adminIconCreatorWorkspace";
  const canvasWrap = document.createElement("div");
  canvasWrap.className = "adminIconCreatorCanvasWrap";
  const viewport = document.createElement("div");
  viewport.className = "adminIconCreatorViewport";
  const displayCanvas = document.createElement("canvas");
  displayCanvas.className = "adminShopCanvas adminIconCreatorCanvas";
  displayCanvas.width = HUB_ICON_W;
  displayCanvas.height = HUB_ICON_H;
  viewport.appendChild(displayCanvas);
  canvasWrap.appendChild(viewport);
  workspace.appendChild(canvasWrap);
  left.appendChild(workspace);

  const actions = document.createElement("div");
  actions.className = "adminIconCreatorActions rgbRow";
  const revertBtn = document.createElement("button");
  revertBtn.type = "button";
  revertBtn.className = "btn";
  revertBtn.textContent = "Revert to default";
  const publishBtn = document.createElement("button");
  publishBtn.type = "button";
  publishBtn.className = "btn green";
  publishBtn.textContent = "Publish";
  actions.appendChild(revertBtn);
  actions.appendChild(publishBtn);
  left.appendChild(actions);

  const status = document.createElement("p");
  status.className = "accessHint adminIconCreatorStatus";
  left.appendChild(status);

  const right = document.createElement("div");
  right.className = "adminIconCreatorPreviewCol";
  const previewTitle = document.createElement("h3");
  previewTitle.textContent = "PLAY hub preview";
  right.appendChild(previewTitle);
  const previewHint = document.createElement("p");
  previewHint.className = "accessHint";
  previewHint.textContent = "Live preview of all category cards (includes unpublished edits in this session).";
  right.appendChild(previewHint);

  const previewShell = document.createElement("div");
  previewShell.className = "adminIconCreatorPreviewShell screen--gameHub";
  const previewGridWrap = document.createElement("div");
  previewGridWrap.className = "gameHubScrollWrap adminIconCreatorPreviewScroll";
  const previewGrid = document.createElement("div");
  previewGrid.className = "gameHubGrid adminIconCreatorPreviewGrid";
  previewGrid.id = "adminIconCreatorPreviewGrid";
  previewGridWrap.appendChild(previewGrid);
  previewShell.appendChild(previewGridWrap);
  right.appendChild(previewShell);

  layout.appendChild(left);
  layout.appendChild(right);
  wrap.appendChild(layout);
  root.appendChild(wrap);

  previewGridWrap.addEventListener(
    "wheel",
    (e) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault();
      previewGridWrap.scrollLeft += e.deltaY;
    },
    { passive: false },
  );

  const sourceRoot = document.getElementById("gameHubGrid") || document.getElementById("gameHubScreen");
  if (sourceRoot) {
    previewGrid.replaceChildren();
    sourceRoot.querySelectorAll<HTMLElement>(".gameHubCard[data-hub-category]").forEach((card) => {
      const clone = card.cloneNode(true) as HTMLElement;
      clone.classList.remove("hidden");
      clone.removeAttribute("aria-hidden");
      clone.tabIndex = -1;
      clone.setAttribute("aria-disabled", "true");
      clone.style.pointerEvents = "none";
      previewGrid.appendChild(clone);
    });
  }

  function syncHistoryButtons() {
    undoBtn.disabled = maskHistoryIndex <= 0;
    redoBtn.disabled = maskHistoryIndex < 0 || maskHistoryIndex >= maskHistory.length - 1;
  }

  function readMaskImageData(): ImageData {
    flushMaskBuf();
    return maskCtx.getImageData(0, 0, HUB_ICON_W, HUB_ICON_H);
  }

  function applyMaskImageData(img: ImageData) {
    maskCtx.putImageData(img, 0, 0);
    invalidateMaskBuf();
    redrawDisplay();
  }

  function resetMaskHistory(initial?: ImageData) {
    maskHistory.length = 0;
    maskHistoryIndex = -1;
    if (initial) {
      maskHistory.push(cloneMaskImageData(initial));
      maskHistoryIndex = 0;
    }
    syncHistoryButtons();
  }

  function pushMaskHistory() {
    const snap = cloneMaskImageData(readMaskImageData());
    if (maskHistoryIndex < maskHistory.length - 1) {
      maskHistory.splice(maskHistoryIndex + 1);
    }
    maskHistory.push(snap);
    if (maskHistory.length > MAX_MASK_HISTORY) {
      maskHistory.shift();
    }
    maskHistoryIndex = maskHistory.length - 1;
    syncHistoryButtons();
  }

  function undoMask() {
    if (maskHistoryIndex <= 0) return;
    maskHistoryIndex -= 1;
    applyMaskImageData(cloneMaskImageData(maskHistory[maskHistoryIndex]!));
    syncHistoryButtons();
    syncSessionPreview();
  }

  function redoMask() {
    if (maskHistoryIndex < 0 || maskHistoryIndex >= maskHistory.length - 1) return;
    maskHistoryIndex += 1;
    applyMaskImageData(cloneMaskImageData(maskHistory[maskHistoryIndex]!));
    syncHistoryButtons();
    syncSessionPreview();
  }

  function endStrokeHistory() {
    commitEdit();
  }

  function syncPreviewIcons() {
    for (const id of HUB_ICON_CATEGORIES) {
      applyHubIconToCategory(id, previewGrid);
    }
  }

  function drawLinePreview(
    dctx: CanvasRenderingContext2D,
    x0: number,
    y0: number,
    x1: number,
    y1: number,
  ) {
    const rect = displayCanvas.getBoundingClientRect();
    const scaleX = displayCanvas.width / rect.width;
    const scaleY = displayCanvas.height / rect.height;
    const alpha = brushOpacity / 255;
    dctx.fillStyle = `rgba(255,255,255,${alpha})`;
    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;
    let x = x0;
    let y = y0;
    const px = Math.max(1, scaleX);
    const py = Math.max(1, scaleY);
    while (true) {
      dctx.fillRect(x * scaleX, y * scaleY, px, py);
      if (x === x1 && y === y1) break;
      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
    }
  }

  function syncFloatingUi() {
    const on = !!floating;
    placeGroup.classList.toggle("hidden", !on);
    if (floating) {
      const base = Math.max(floating.source.width, floating.source.height);
      const cur = Math.max(floating.w, floating.h);
      const pct = Math.round((cur / base) * 100);
      importScaleSlider.value = String(clamp(pct, 10, 200));
      importScaleDisplay.textContent = `${clamp(pct, 10, 200)}%`;
    }
  }

  function stampFloating() {
    if (!floating) return;
    flushMaskBuf();
    const tmp = document.createElement("canvas");
    tmp.width = floating.source.width;
    tmp.height = floating.source.height;
    tmp.getContext("2d")!.putImageData(floating.source, 0, 0);
    maskCtx.drawImage(tmp, floating.x, floating.y, floating.w, floating.h);
    invalidateMaskBuf();
    floating = null;
    floatDrag = null;
    syncFloatingUi();
    redrawDisplay();
    commitEdit();
    status.textContent = "Imported image placed — Import again to add another, then Publish.";
  }

  function discardFloating() {
    floating = null;
    floatDrag = null;
    syncFloatingUi();
    redrawDisplay();
    status.textContent = "Import discarded.";
  }

  function setFloatingFromMask(source: ImageData) {
    const maxFit = Math.min(HUB_ICON_W / source.width, HUB_ICON_H / source.height, 1);
    const w = Math.max(1, Math.round(source.width * maxFit));
    const h = Math.max(1, Math.round(source.height * maxFit));
    floating = {
      source,
      x: Math.round((HUB_ICON_W - w) / 2),
      y: Math.round((HUB_ICON_H - h) / 2),
      w,
      h,
    };
    floatDrag = null;
    syncFloatingUi();
    redrawDisplay();
    status.textContent = "Drag to move, use Import size / corner to resize, then Place. Import again to pick another image.";
  }

  function hitFloating(px: number, py: number): "move" | "resize" | null {
    if (!floating) return null;
    const { x, y, w, h } = floating;
    const handle = 10;
    if (px >= x + w - handle && px <= x + w + 2 && py >= y + h - handle && py <= y + h + 2) {
      return "resize";
    }
    if (px >= x && px < x + w && py >= y && py < y + h) return "move";
    return null;
  }

  function drawFloatingOverlay(dctx: CanvasRenderingContext2D) {
    if (!floating) return;
    const tmp = document.createElement("canvas");
    tmp.width = floating.source.width;
    tmp.height = floating.source.height;
    tmp.getContext("2d")!.putImageData(floating.source, 0, 0);
    const sx = displayCanvas.width / HUB_ICON_W;
    const sy = displayCanvas.height / HUB_ICON_H;
    dctx.save();
    dctx.imageSmoothingEnabled = false;
    dctx.drawImage(
      tmp,
      floating.x * sx,
      floating.y * sy,
      floating.w * sx,
      floating.h * sy,
    );
    dctx.strokeStyle = "rgba(102,255,255,0.95)";
    dctx.lineWidth = Math.max(1, sx);
    dctx.setLineDash([4 * sx, 3 * sx]);
    dctx.strokeRect(floating.x * sx, floating.y * sy, floating.w * sx, floating.h * sy);
    dctx.setLineDash([]);
    const hs = Math.max(6, 8 * sx);
    dctx.fillStyle = "rgba(102,255,255,0.95)";
    dctx.fillRect(
      (floating.x + floating.w) * sx - hs,
      (floating.y + floating.h) * sy - hs,
      hs,
      hs,
    );
    dctx.restore();
  }

  function redrawDisplay(previewLineEnd: [number, number] | null = null) {
    flushMaskBuf();
    const dctx = displayCanvas.getContext("2d")!;
    dctx.imageSmoothingEnabled = false;
    dctx.fillStyle = "#1a1a22";
    dctx.fillRect(0, 0, displayCanvas.width, displayCanvas.height);
    dctx.drawImage(maskCanvas, 0, 0, displayCanvas.width, displayCanvas.height);
    if (previewLineEnd && lineStart) {
      drawLinePreview(dctx, lineStart[0], lineStart[1], previewLineEnd[0], previewLineEnd[1]);
    }
    drawFloatingOverlay(dctx);
  }

  function clearMask() {
    maskCtx.clearRect(0, 0, HUB_ICON_W, HUB_ICON_H);
    invalidateMaskBuf();
  }

  function setMaskAlpha(x: number, y: number, alpha: number) {
    if (x < 0 || y < 0 || x >= HUB_ICON_W || y >= HUB_ICON_H) return;
    const buf = ensureMaskBuf();
    const i = (y * HUB_ICON_W + x) * 4;
    if (alpha <= 0) {
      buf.data[i + 3] = 0;
      return;
    }
    buf.data[i] = 255;
    buf.data[i + 1] = 255;
    buf.data[i + 2] = 255;
    buf.data[i + 3] = clamp(alpha, 0, 255);
  }

  function paintBrush(cx: number, cy: number, erasing: boolean) {
    const alpha = erasing ? 0 : brushOpacity;
    const r = Math.max(0, pencilSize - 1);
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx * dx + dy * dy <= r * r + r * 0.25) setMaskAlpha(cx + dx, cy + dy, alpha);
      }
    }
  }

  function drawLine(x0: number, y0: number, x1: number, y1: number, erasing: boolean) {
    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;
    let x = x0;
    let y = y0;
    while (true) {
      paintBrush(x, y, erasing);
      if (x === x1 && y === y1) break;
      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
    }
  }

  function floodFill(sx: number, sy: number, fillAlpha: number) {
    flushMaskBuf();
    const img = maskCtx.getImageData(0, 0, HUB_ICON_W, HUB_ICON_H);
    maskBuf = img;
    const targetA = img.data[(sy * HUB_ICON_W + sx) * 4 + 3]!;
    if (targetA === fillAlpha) return;
    const stack: Array<[number, number]> = [[sx, sy]];
    const seen = new Uint8Array(HUB_ICON_W * HUB_ICON_H);
    while (stack.length) {
      const [x, y] = stack.pop()!;
      const idx = y * HUB_ICON_W + x;
      if (seen[idx]) continue;
      seen[idx] = 1;
      const pi = idx * 4 + 3;
      if (img.data[pi] !== targetA) continue;
      if (fillAlpha <= 0) {
        img.data[idx * 4 + 3] = 0;
      } else {
        img.data[idx * 4] = 255;
        img.data[idx * 4 + 1] = 255;
        img.data[idx * 4 + 2] = 255;
        img.data[idx * 4 + 3] = fillAlpha;
      }
      if (x > 0) stack.push([x - 1, y]);
      if (x < HUB_ICON_W - 1) stack.push([x + 1, y]);
      if (y > 0) stack.push([x, y - 1]);
      if (y < HUB_ICON_H - 1) stack.push([x, y + 1]);
    }
  }

  function canvasCoords(ev: MouseEvent | Touch): [number, number] | null {
    const rect = displayCanvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return null;
    const px = Math.floor(((ev.clientX - rect.left) / rect.width) * HUB_ICON_W);
    const py = Math.floor(((ev.clientY - rect.top) / rect.height) * HUB_ICON_H);
    if (px < 0 || py < 0 || px >= HUB_ICON_W || py >= HUB_ICON_H) return null;
    return [px, py];
  }

  function syncSessionPreview() {
    flushMaskBuf();
    const img = maskCtx.getImageData(0, 0, HUB_ICON_W, HUB_ICON_H);
    if (maskImageDataEmpty(img)) {
      setHubIconSessionOverride(selectedCategory, null);
    } else {
      const normalized = normalizeMaskImageData(img);
      maskCtx.putImageData(normalized, 0, 0);
      invalidateMaskBuf();
      setHubIconSessionOverride(selectedCategory, maskDataUrlFromImageData(normalized));
    }
    syncPreviewIcons();
    refreshAllHubIcons();
  }

  function commitEdit() {
    syncSessionPreview();
    pushMaskHistory();
  }

  function loadCategoryIntoEditor(cat: HubIconCategoryId) {
    selectedCategory = cat;
    floating = null;
    floatDrag = null;
    syncFloatingUi();
    clearMask();
    const mask = published.get(cat);
    if (mask) {
      void loadImageDataFromDataUrl(mask).then((img) => {
        const fitted = fitMaskImageDataToHubCanvas(img);
        maskCtx.putImageData(fitted, 0, 0);
        invalidateMaskBuf();
        redrawDisplay();
        resetMaskHistory(readMaskImageData());
        syncSessionPreview();
      });
    } else {
      redrawDisplay();
      resetMaskHistory(readMaskImageData());
      syncSessionPreview();
    }
    const hasCustom = published.has(cat);
    status.textContent = hasCustom
      ? `Editing custom icon for ${HUB_ICON_CATEGORY_LABELS[cat]}.`
      : `Editing default slot for ${HUB_ICON_CATEGORY_LABELS[cat]} (original SVG shown until you publish).`;
  }

  async function refreshPublished() {
    try {
      const rows = await fetchAdminHubIcons();
      published.clear();
      rows.forEach((r) => published.set(r.categoryId, r.maskData));
      rows.forEach((r) => setPublishedHubIcon(r.categoryId, r.maskData, r.updatedAt));
      loadCategoryIntoEditor(selectedCategory);
      syncPreviewIcons();
    } catch {
      status.textContent = "Could not load published icons.";
    }
  }

  function openImportPreviewModal(img: HTMLImageElement, fileName: string) {
    const resolutions = hubIconResolutionOptions();
    let chosen = resolutions[resolutions.length - 1] ?? { w: HUB_ICON_W, h: HUB_ICON_H };

    const modal = document.createElement("div");
    modal.className = "adminShopPublishModal adminShopImportModal screen";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    const box = document.createElement("div");
    box.className = "modeDetailModalPanel modeBox adminShopImportModalPanel";
    box.innerHTML = `<h2>Import image</h2>
      <p class="accessHint">The image is converted to a luminance mask — brightness becomes shade level (alpha). Adjust resolution, then Continue to edit.</p>`;

    const meta = document.createElement("p");
    meta.className = "accessHint adminShopImportMeta";
    meta.textContent = `${fileName || "image"} — source ${img.naturalWidth}×${img.naturalHeight}`;
    box.appendChild(meta);

    const previewWrap = document.createElement("div");
    previewWrap.className = "adminShopImportPreviewWrap";
    const previewCanvas = document.createElement("canvas");
    previewCanvas.className = "adminShopImportPreviewCanvas";
    box.appendChild(previewWrap);
    previewWrap.appendChild(previewCanvas);

    const resSelect = document.createElement("select");
    resSelect.className = "formInput adminShopToolSelect";
    resolutions.forEach(({ w, h, label }) => {
      const o = document.createElement("option");
      o.value = `${w}x${h}`;
      o.textContent = label;
      resSelect.appendChild(o);
    });
    resSelect.value = `${chosen.w}x${chosen.h}`;
    const resRow = document.createElement("div");
    resRow.className = "formRow";
    resRow.innerHTML = `<span class="formLabel">Resolution</span>`;
    resRow.appendChild(resSelect);
    box.appendChild(resRow);

    const actions = document.createElement("div");
    actions.className = "rgbRow";
    actions.style.justifyContent = "flex-end";
    actions.style.gap = "8px";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "btn";
    cancelBtn.textContent = "Cancel";
    const continueBtn = document.createElement("button");
    continueBtn.type = "button";
    continueBtn.className = "btn green";
    continueBtn.textContent = "Continue";
    const chooseAnotherBtn = document.createElement("button");
    chooseAnotherBtn.type = "button";
    chooseAnotherBtn.className = "btn";
    chooseAnotherBtn.textContent = "Choose another";
    actions.appendChild(cancelBtn);
    actions.appendChild(chooseAnotherBtn);
    actions.appendChild(continueBtn);
    box.appendChild(actions);
    modal.appendChild(box);
    document.body.appendChild(modal);

    const renderPreview = () => {
      const raw = downscaleImageToHubIconGrid(img, chosen.w, chosen.h);
      const toneMask = imageDataToLuminanceMask(raw);
      previewCanvas.width = chosen.w;
      previewCanvas.height = chosen.h;
      const ctx = previewCanvas.getContext("2d")!;
      ctx.imageSmoothingEnabled = false;
      ctx.fillStyle = "#1a1a22";
      ctx.fillRect(0, 0, chosen.w, chosen.h);
      ctx.putImageData(toneMask, 0, 0);
      const displayPx = Math.min(280, Math.max(120, chosen.w * 2));
      previewCanvas.style.width = `${displayPx}px`;
      previewCanvas.style.height = `${Math.round(displayPx * (chosen.h / chosen.w))}px`;
    };

    resSelect.onchange = () => {
      const [w, h] = resSelect.value.split("x").map(Number);
      chosen = { w: w || HUB_ICON_W, h: h || HUB_ICON_H };
      renderPreview();
    };

    const close = () => modal.remove();
    cancelBtn.onclick = () => close();
    chooseAnotherBtn.onclick = () => {
      close();
      importFileInput.click();
    };
    continueBtn.onclick = () => {
      const raw = downscaleImageToHubIconGrid(img, chosen.w, chosen.h);
      const toneMask = imageDataToLuminanceMask(raw);
      setFloatingFromMask(toneMask);
      close();
    };

    renderPreview();
  }

  displayCanvas.addEventListener("mousedown", (ev) => {
    ev.preventDefault();
    if (ev.button !== 0) return;
    const c = canvasCoords(ev);
    if (!c) return;
    const [x, y] = c;

    if (floating) {
      const hit = hitFloating(x, y);
      if (hit) {
        floatDrag = {
          mode: hit,
          startX: x,
          startY: y,
          origX: floating.x,
          origY: floating.y,
          origW: floating.w,
          origH: floating.h,
        };
        drawing = true;
        return;
      }
      // Click outside floating → stamp, then continue with tool
      stampFloating();
    }

    drawing = true;
    if (tool === "line") {
      lineStart = [x, y];
      lineEnd = [x, y];
    } else if (tool === "bucket") {
      floodFill(x, y, brushOpacity);
      redrawDisplay();
      endStrokeHistory();
      drawing = false;
    } else {
      paintBrush(x, y, tool === "eraser");
      redrawDisplay();
    }
  });
  window.addEventListener("mousemove", (ev) => {
    if (!drawing) return;
    const c = canvasCoords(ev);
    if (!c) return;
    const [x, y] = c;
    if (floating && floatDrag) {
      const dx = x - floatDrag.startX;
      const dy = y - floatDrag.startY;
      if (floatDrag.mode === "move") {
        floating.x = floatDrag.origX + dx;
        floating.y = floatDrag.origY + dy;
      } else {
        const aspect = floatDrag.origH / Math.max(1, floatDrag.origW);
        const nw = Math.max(8, floatDrag.origW + dx);
        floating.w = Math.round(nw);
        floating.h = Math.round(nw * aspect);
        syncFloatingUi();
      }
      redrawDisplay();
      return;
    }
    if (tool === "line" && lineStart) {
      lineEnd = [x, y];
      redrawDisplay(lineEnd);
    } else if (tool === "pencil" || tool === "eraser") {
      paintBrush(x, y, tool === "eraser");
      redrawDisplay();
    }
  });
  window.addEventListener("mouseup", () => {
    if (!drawing) return;
    if (floating && floatDrag) {
      floatDrag = null;
      drawing = false;
      return;
    }
    if (tool === "line" && lineStart && lineEnd) {
      drawLine(lineStart[0], lineStart[1], lineEnd[0], lineEnd[1], false);
      redrawDisplay();
      endStrokeHistory();
    } else if (tool === "pencil" || tool === "eraser") {
      endStrokeHistory();
    }
    drawing = false;
    lineStart = null;
    lineEnd = null;
  });

  placeBtn.onclick = () => stampFloating();
  discardImportBtn.onclick = () => discardFloating();
  importScaleSlider.oninput = () => {
    if (!floating) return;
    const pct = Number(importScaleSlider.value) || 100;
    importScaleDisplay.textContent = `${pct}%`;
    const baseW = floating.source.width;
    const baseH = floating.source.height;
    const nw = Math.max(8, Math.round((baseW * pct) / 100));
    const nh = Math.max(8, Math.round((baseH * pct) / 100));
    const cx = floating.x + floating.w / 2;
    const cy = floating.y + floating.h / 2;
    floating.w = nw;
    floating.h = nh;
    floating.x = Math.round(cx - nw / 2);
    floating.y = Math.round(cy - nh / 2);
    redrawDisplay();
  };

  undoBtn.onclick = () => undoMask();
  redoBtn.onclick = () => redoMask();
  centerBtn.onclick = () => {
    flushMaskBuf();
    const img = readMaskImageData();
    if (maskImageDataEmpty(img)) {
      status.textContent = "Nothing to center — draw or import a mask first.";
      return;
    }
    const centered = centerMaskImageData(img);
    applyMaskImageData(centered);
    endStrokeHistory();
    status.textContent = "Drawing centered in the category art area.";
  };

  toolSelect.onchange = () => {
    tool = toolSelect.value as Tool;
  };
  sizeSlider.oninput = () => {
    pencilSize = Number(sizeSlider.value) || 1;
    sizeDisplay.textContent = `${pencilSize}px`;
  };
  shadeSlider.oninput = () => {
    brushOpacity = Number(shadeSlider.value) || 255;
    shadeDisplay.textContent = `${Math.round((brushOpacity / 255) * 100)}%`;
  };
  catSelect.onchange = () => {
    loadCategoryIntoEditor(catSelect.value as HubIconCategoryId);
  };

  importBtn.onclick = () => importFileInput.click();
  importFileInput.onchange = () => {
    const file = importFileInput.files?.[0];
    importFileInput.value = "";
    if (!file) return;
    const err = validateImportImageFile(file);
    if (err) {
      status.textContent = err;
      return;
    }
    void loadImageFromFile(file)
      .then((img) => openImportPreviewModal(img, file.name))
      .catch(() => {
        status.textContent = "Could not read that image.";
      });
  };

  revertBtn.onclick = () => {
    void (async () => {
      try {
        await revertAdminHubIcon(selectedCategory);
        published.delete(selectedCategory);
        removePublishedHubIcon(selectedCategory);
        clearMask();
        redrawDisplay();
        resetMaskHistory(readMaskImageData());
        setHubIconSessionOverride(selectedCategory, null);
        syncPreviewIcons();
        refreshAllHubIcons();
        status.textContent = `${HUB_ICON_CATEGORY_LABELS[selectedCategory]} reverted to default icon.`;
      } catch {
        status.textContent = "Revert failed.";
      }
    })();
  };

  publishBtn.onclick = () => {
    void (async () => {
      if (floating) stampFloating();
      flushMaskBuf();
      const img = maskCtx.getImageData(0, 0, HUB_ICON_W, HUB_ICON_H);
      if (maskImageDataEmpty(img)) {
        status.textContent = "Draw or import a mask before publishing.";
        return;
      }
      const normalized = normalizeMaskImageData(img);
      const maskData = maskDataUrlFromImageData(normalized);
      try {
        const row = await publishAdminHubIcon({ categoryId: selectedCategory, maskData });
        published.set(selectedCategory, row.maskData);
        setPublishedHubIcon(selectedCategory, row.maskData, row.updatedAt);
        setHubIconSessionOverride(selectedCategory, row.maskData);
        syncPreviewIcons();
        refreshAllHubIcons();
        status.textContent = `Published icon for ${HUB_ICON_CATEGORY_LABELS[selectedCategory]}.`;
      } catch {
        status.textContent = "Publish failed.";
      }
    })();
  };

  void refreshPublished();

  return {
    refresh: () => {
      void refreshPublished();
    },
  };
}
